import React, { useState, useEffect } from 'react';
import { MaterialReactTable } from 'material-react-table';
import { Typography, CircularProgress, Button, Box, TextField, IconButton, Tooltip } from '@mui/material';
import { Visibility as VisibilityIcon, Check as CheckIcon, Clear as ClearIcon, Download as DownloadIcon, MoreHoriz as MoreIcon } from '@mui/icons-material';
import { ModalGeneral } from '../../Componentes/ModalGeneral';
import DetallesTraduccionModal from '../molecules/DetallesTraduccionModal';
import { MRT_Localization_ES } from 'material-react-table/locales/es';
import { useTranslation } from '../translationContext';
import { useBody } from '../../Body/state/BodyContext';
import useMediaQuery from '@mui/material/useMediaQuery';

const AutorizacionTextos = () => {
    const { getTranslationsByStatus, downloadTranslation, updateTranslation } = useTranslation();
    const { setMessage, setSuccess } = useBody();
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);
    const [editedRows, setEditedRows] = useState({});
    const matches = useMediaQuery('(min-width:1185px)');

    const fetchTranslations = async () => {
        try {
            setLoading(true);
            const translations = await getTranslationsByStatus(1);
            setData(translations);
        } catch (error) {
            console.error('Error fetching translations:', error);
            setMessage('Error al cargar las traducciones a autorizar');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTranslations();
    }, [refresh]);

    const handleInputChange = (rowId, field, value) => {
        setEditedRows((prev) => ({
            ...prev,
            [rowId]: {
                ...prev[rowId],
                [field]: value,
            },
        }));
    };

    const handleSubmit = async (row, status_id) => {
        const { id } = row.original;
        const { word_quantity, observation } = editedRows[id] || {};

        const translationData = {
            word_quantity: word_quantity ? word_quantity : 0,
            observation: observation ? observation : "",
            status_id
        };
        try {
            handleCloseDetailsModal();
            setLoading(true);
            await updateTranslation(translationData, id);
            if (status_id === 2) setSuccess('Traducción aprobada correctamente');
            if (status_id === 3) setSuccess('Traducción rechazada correctamente');
        } catch (error) {
            console.error('Error al actualizar la traducción:', error);
            setMessage('Error al actualizar la traducción');
        } finally {
            setRefresh((prev) => !prev);
            setLoading(false);
        }
    };

    const handleOpenDetailsModal = (row) => {
        setSelectedDetail(row);
        setOpenDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setOpenDetailsModal(false);
        setSelectedDetail(null);
    };

    const handleDownloadFile = async (type, folder, fileName) => {
        try {
            const response = await downloadTranslation(type, folder, fileName);
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            setSuccess('Archivo descargado correctamente');
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            setMessage('Error al descargar el archivo');
        }
    };

    const columns = [
        {
            header: 'Detalles',
            size: matches ? 160 : 100,
            Cell: ({ cell }) => (
                <Button
                    sx={{ textTransform: 'none' }}
                    onClick={() => handleOpenDetailsModal(cell.row)}>
                    {matches ?
                        <VisibilityIcon sx={{ marginRight: "7px" }} />
                        :
                        <MoreIcon />
                    }
                    {matches ? "Ver Detalles" : ""}
                </Button>
            ),
        },
        {
            header: 'Solicitante',
            accessorKey: 'petitioner_name',
            size: 120,
            Cell: ({ cell }) => (
                <Tooltip title={cell.getValue()} placement="bottom-start">
                    <span>{cell.getValue()}</span>
                </Tooltip>
            ),
        },
        {
            header: 'Documento',
            accessorKey: 'file_original.file_name',
            size: 120,
            Cell: ({ cell }) => {
                const fileName = cell.getValue();
                const folderName = cell.row.original.file_original.folder_name;
                return fileName ? (
                    <Button
                        variant="outlined"
                        sx={{ textTransform: 'none' }}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFile('original', folderName, fileName)}
                    >
                        Descargar
                    </Button>
                ) : "Sin archivo adjuntado";
            },
        },
        {
            header: 'Cantidad de palabras',
            accessorKey: 'word_quantity',
            Cell: ({ cell }) => (
                <TextField
                    type="number"
                    size="small"
                    value={editedRows[cell.row.id]?.word_quantity || cell.getValue()}
                    onChange={(e) => handleInputChange(cell.row.original.id, 'word_quantity', e.target.value)}
                />
            ),
        },
        {
            header: 'Observaciones',
            accessorKey: 'observation',
            Cell: ({ cell }) => (
                <TextField
                    size="small"
                    value={editedRows[cell.row.id]?.observation || cell.getValue()}
                    onChange={(e) => handleInputChange(cell.row.original.id, 'observation', e.target.value)}
                />
            ),
        },
        {
            header: 'Autorización',
            Cell: ({ row }) => (
                <>
                    <IconButton onClick={() => handleSubmit(row, 2)} color="success">
                        <CheckIcon />
                    </IconButton>
                    <IconButton onClick={() => handleSubmit(row, 3)} color="error">
                        <ClearIcon />
                    </IconButton>
                </>
            ),
        },
    ];

    const responsiveColumns = columns.slice(0, -3);

    return (
        <>
            <Box marginBottom={"10%"}>
                <Typography fontSize={matches ? "30px" : "28px"} my="40px" mx="10px">
                    Autorización de Textos
                </Typography>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <MaterialReactTable
                        columns={matches ? columns : responsiveColumns}
                        enableColumnResizing={matches ? false : true}
                        data={data}
                        enableFullScreenToggle={false}
                        localization={MRT_Localization_ES}
                        enableRowSelection={false}
                        enablePagination={false}
                        enableSorting
                        layoutMode={"grid"}
                    />
                )}
            </Box>

            <ModalGeneral
                Contenido={<DetallesTraduccionModal
                    editedRows={editedRows}
                    handleInputChange={handleInputChange}
                    handleSubmit={handleSubmit}
                    autorizacion={true}
                    selectedDetail={selectedDetail}
                />}
                open={openDetailsModal}
                onClose={handleCloseDetailsModal}
                titulo={"Detalles de la Traducción"}
                heightAuto
            />
        </>
    );
};

export default AutorizacionTextos;
